import React, { useEffect, useMemo, useState } from "react"
import axios from "axios"
import Swal from "sweetalert2"
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Label,
  Row,
  Spinner,
} from "reactstrap"
import { Modal, ModalHeader, ModalBody } from "reactstrap"
import { useLocation, useNavigate } from "react-router-dom"
import { FaPlus, FaRegTrashAlt } from "react-icons/fa"
import AtsLogo from "../../assets/images/ats-logo2.png"

const toQuestionMap = job => {
  const source = job?.second_screening_questions || {}
  const entries = Object.entries(source)

  if (!entries.length) {
    return { general: [""] }
  }

  return entries.reduce((acc, [category, questions]) => {
    acc[category] =
      Array.isArray(questions) && questions.length ? questions : [""]
    return acc
  }, {})
}

const getTechSkillsSection = job =>
  Array.isArray(job?.time_settings)
    ? job.time_settings.find(
        section =>
          String(section.section_name || section.name || "")
            .toLowerCase()
            .trim() === "techskills"
      )
    : null
const levelBadgeStyle = level => ({
  display: "inline-block",
  padding: "4px 10px",
  borderRadius: 20,
  fontSize: 12,
  fontWeight: 600,
  marginBottom: 6,
  background:
    level === "beginner"
      ? "#e6f4ea"
      : level === "intermediate"
      ? "#fff4e5"
      : "#fdecea",
  color:
    level === "beginner"
      ? "#1e7e34"
      : level === "intermediate"
      ? "#b26a00"
      : "#c62828",
})

const toSkillQuestionMap = job => {
  const source =
    getTechSkillsSection(job)?.skill_wise_questions || job?.skill_wise_questions || {}

  return Object.entries(source).reduce((acc, [skill, details]) => {
    if (!skill) return acc

    acc[skill] = {
      level: details?.level || "",
      questions: {
        beginner: Array.isArray(details?.questions?.beginner)
          ? details.questions.beginner
          : [],
        intermediate: Array.isArray(details?.questions?.intermediate)
          ? details.questions.intermediate
          : [],
        advanced: Array.isArray(details?.questions?.advanced)
          ? details.questions.advanced
          : [],
      },
    }

    return acc
  }, {})
}

const toSectionQuestionMap = job => {
  const sections = Array.isArray(job?.time_settings) ? job.time_settings : []

  return sections.reduce((acc, section) => {
    const name = section.section_name || section.name || `section_${section.section}`
    if (name.toLowerCase().trim() === "techskills") return acc
    const sectionKey = Object.keys(section.manual_questions || {})[0]
    const questions = sectionKey
      ? section.manual_questions[sectionKey]?.questions || {}
      : {}

    acc[name] = {
      section,
      questions: {
        beginner: Array.isArray(questions.beginner) ? questions.beginner : [],
        intermediate: Array.isArray(questions.intermediate) ? questions.intermediate : [],
        advanced: Array.isArray(questions.advanced) ? questions.advanced : [],
      },
    }

    return acc
  }, {})
}

const formatCategoryLabel = (value = "") =>
  value.replace(/_/g, " ").replace(/\b\w/g, char => char.toUpperCase())

const stripHtml = (value = "") =>
  value
    .replace(/<style[^>]*>[\s\S]*?<\/style>/gi, " ")
    .replace(/<script[^>]*>[\s\S]*?<\/script>/gi, " ")
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/\s+/g, " ")
    .trim()

const normalizeQuestions = questions =>
  Object.entries(questions || {}).reduce((acc, [category, values]) => {
    const filteredQuestions = (Array.isArray(values) ? values : [])
      .map(item => (typeof item === "string" ? item.trim() : ""))
      .filter(Boolean)

    if (filteredQuestions.length) {
      acc[category] = filteredQuestions
    }

    return acc
  }, {})

const normalizeNestedQuestions = nested =>
  Object.entries(nested || {}).reduce((acc, [key, section]) => {
    const questions = section?.questions || {}
    const cleaned = ["beginner", "intermediate", "advanced"].reduce(
      (sectionAcc, level) => {
        const values = Array.isArray(questions[level]) ? questions[level] : []
        const filtered = values
          .map(item => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean)

        if (filtered.length) {
          sectionAcc[level] = filtered
        }

        return sectionAcc
      },
      {}
    )

    if (Object.keys(cleaned).length) {
      acc[key] = {
        // ...section,
        questions: cleaned,
      }
    }

    return acc
  }, {})

const toSkillReviewPayload = nested =>
  Object.entries(nested || {}).reduce((skills, [skill, details]) => {
    const questions = details?.questions || {}
    const levels = ["beginner", "intermediate", "advanced"].reduce(
      (levelPayload, level) => {
        const values = Array.isArray(questions[level]) ? questions[level] : []
        const cleaned = values
          .map(item => (typeof item === "string" ? item.trim() : ""))
          .filter(Boolean)
          .map(question => ({ question }))

        if (cleaned.length) levelPayload[level] = cleaned
        return levelPayload
      },
      {}
    )

    if (Object.keys(levels).length) {
      skills.push({ skill, ...levels })
    }

    return skills
  }, [])

const JobOwnerScreeningQuestions = () => {
  document.title = "ATS - Job Owner Screening Questions"

  const location = useLocation()
  const navigate = useNavigate()
  const params = useMemo(() => new URLSearchParams(location.search), [location.search])
  const jobId = params.get("job_id") || location.state?.job?.id || location.state?.job_id || ""
  const [job, setJob] = useState(location.state?.job || null)
  const [loadingJob, setLoadingJob] = useState(Boolean(jobId))
  const [finalizing, setFinalizing] = useState(false)
  const [isApproved, setIsApproved] = useState(false)
  const [mainTab, setMainTab] = useState("second")
  const [originalSkillQuestions, setOriginalSkillQuestions] = useState(() =>
    toSkillQuestionMap(job)
  )

  const [originalSectionQuestions, setOriginalSectionQuestions] = useState(() =>
    toSectionQuestionMap(job)
  )
useEffect(() => {
  const parsedQuestions = toQuestionMap(job)

  setScreeningQuestions(parsedQuestions)
  setOriginalQuestions(parsedQuestions)

  const skills = toSkillQuestionMap(job)
  const sections = toSectionQuestionMap(job)

  setSkillQuestions(skills)
  setSectionQuestions(sections)

  setOriginalSkillQuestions(skills)
  setOriginalSectionQuestions(sections)

}, [job])
const isEqual = (a, b) => JSON.stringify(a) === JSON.stringify(b)
  const [subTab, setSubTab] = useState("skills")
  const [focusTarget, setFocusTarget] = useState(null)
  const [isTokenValid, setIsTokenValid] = useState(null);
  const [showPreview, setShowPreview] = useState(false)
const [previewTab, setPreviewTab] = useState("second") // second | skills | sections
  const [talentTab, setTalentTab] = useState("skills")
  useEffect(() => {
    const checkToken = async () => {
      const token = params.get("token");
      if (!token) {
        setIsTokenValid(false);
        return;
      }
      try {
        const response = await axios.get(
          `/api/v1/check-screening-token/?token=${token}`,
        );
        setIsTokenValid(response.data.valid);
      } catch (error) {
        setIsTokenValid(false);
      }
    };
    checkToken();
  }, [params]);

  const jobDescription = stripHtml(job?.job_description || job?.jd || "")
  const [screeningQuestions, setScreeningQuestions] = useState(() =>
    toQuestionMap(job)
  )
  const [originalQuestions, setOriginalQuestions] = useState(() =>
    toQuestionMap(job)
  )
  const [skillQuestions, setSkillQuestions] = useState(() =>
    toSkillQuestionMap(job)
  )
  const [sectionQuestions, setSectionQuestions] = useState(() =>
    toSectionQuestionMap(job)
  )

  const questionCategories = useMemo(
    () => Object.keys(screeningQuestions),
    [screeningQuestions]
  )

  useEffect(() => {
      const parsedQuestions = toQuestionMap(job)
      setScreeningQuestions(parsedQuestions)
      setOriginalQuestions(parsedQuestions)
    setSkillQuestions(toSkillQuestionMap(job))
    setSectionQuestions(toSectionQuestionMap(job))
  }, [job])

  useEffect(() => {
    if (!loadingJob && !job?.job_title && !questionCategories.length) {
      Swal.fire({
        icon: "warning",
        title: "Job Not Found",
        text: "This review link is missing the required job details.",
      }).then(() => navigate("/all-job-orders"))
    }
  }, [job, loadingJob, navigate, questionCategories.length])

  useEffect(() => {
    let isMounted = true

    const fetchJobDetails = async () => {
      if (!jobId) {
        setLoadingJob(false)
        return
      }

      setLoadingJob(true)

      try {
        const response = await axios.get(`/api/v1/job-screening-settings/${jobId}/`)
        const payload = response?.data || {}
        const jobData = payload?.data || payload

        if (jobData && isMounted) {
          const normalizedJob = {
            ...jobData,
            id: jobData.id ?? jobData.job_id ?? jobId,
          }

          setJob(normalizedJob)
          setLoadingJob(false)
          return
        }
      } catch (error) {
        if (isMounted) {
          setLoadingJob(false)
        }
      }

      if (isMounted) {
        setLoadingJob(false)
      }
    }

    fetchJobDetails()

    return () => {
      isMounted = false
    }
  }, [jobId])
  const mergeIntoTimeSettings = (timeSettings, skillQuestions, sectionQuestions) => {
    return (timeSettings || []).map(section => {
      const sectionName =
      section.section_name || section.name || section.section || "";

    const normalizedName = String(sectionName).toLowerCase().trim();

      if (normalizedName === "techskills") {
        return {
          ...section,
          skill_wise_questions: skillQuestions,
      };
      }
      if (sectionQuestions[sectionName]) {
        return {
          ...section,
          manual_questions: {
            ...section.manual_questions,
            questions: sectionQuestions[sectionName].questions,
          },
      };
      }
    return section;
  });
};
  const handleFinalize = async () => {
    setFinalizing(true)
    const questions = normalizeQuestions(screeningQuestions)
    const skillPayload = normalizeNestedQuestions(skillQuestions)
    const sectionPayload = normalizeNestedQuestions(sectionQuestions)

    const originalSkillPayload = normalizeNestedQuestions(originalSkillQuestions)
    const originalSectionPayload = normalizeNestedQuestions(originalSectionQuestions)

    const aiChanged = !isEqual(questions, originalQuestions)
    const skillChanged = !isEqual(skillPayload, originalSkillPayload)
    const sectionChanged = !isEqual(sectionPayload, originalSectionPayload)
    if (aiChanged && !skillChanged && !sectionChanged) {
      await Swal.fire({
        icon: "warning",
        title: "Incomplete Review",
        text: "You only updated AI Generated Questions. Please review Video Questions (Skill/Section) before approving.",
        confirmButtonText: "OK",
      })
        setFinalizing(false);
      return
    }
    try {
      const questions = normalizeQuestions(screeningQuestions)
      const skillPayload = normalizeNestedQuestions(skillQuestions)
      const skillReviewPayload = toSkillReviewPayload(skillPayload)
      const token = params.get("token");
      const sectionPayload = normalizeNestedQuestions(sectionQuestions)
      const hasQuestions =
        Object.keys(questions).length > 0 ||
        skillReviewPayload.length > 0 ||
        Object.keys(sectionPayload).length > 0

      if (!job?.id) {
        throw new Error("Missing job id")
      }
      const updatedTimeSettings = mergeIntoTimeSettings(
        job?.time_settings || [],
        skillPayload,
        sectionPayload
    );


      if (hasQuestions) {
        const reviewPayload = {
          job_id: job?.id,
          second_screening_questions: questions,
          skill_wise_questions: skillReviewPayload,
          section_wise_questions: sectionPayload,
          time_settings:updatedTimeSettings,
          token:token
        }

        await axios.patch(`/api/v1/update-second-screening-qs/`, reviewPayload)
        setOriginalQuestions(questions)
      }

      if (hasQuestions) {
        await axios.post(`/api/v1/notify-recruiters/?job_id=${job?.id}`)
        setIsApproved(true)
        return
      }

      await Swal.fire({
        icon: "info",
        title: "No Questions",
        text: "Add at least one valid question before finalizing.",
        confirmButtonText: "OK",
      })
    } catch (error) {
      await Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          error.response?.data?.message ||
          error.message ||
          "Unable to update second screening questions.",
      })
    } finally {
      setFinalizing(false)
    }
  }

  const handleEditQuestion = (category, index, value) => {
    setScreeningQuestions(prev => ({
      ...prev,
      [category]: (prev[category] || []).map((question, idx) =>
        idx === index ? value : question
      ),
    }))
  }

  const handleDeleteQuestion = (category, index) => {
    setScreeningQuestions(prev => {
      const updatedQuestions = (prev[category] || []).filter(
        (_, idx) => idx !== index
      )

      return {
        ...prev,
        [category]: updatedQuestions.length ? updatedQuestions : [""],
      }
    })
  }

  const handleAddQuestion = category => {
    setScreeningQuestions(prev => {
      const nextQuestions = [...(prev[category] || []), ""]
      setFocusTarget({ type: "second", category, index: nextQuestions.length - 1 })
      return {
        ...prev,
        [category]: nextQuestions,
      }
    })
  }

  const handleEditSkillQuestion = (skill, level, index, value) => {
    setSkillQuestions(prev => ({
      ...prev,
      [skill]: {
        ...prev[skill],
        questions: {
          ...prev[skill].questions,
          [level]: prev[skill].questions[level].map((question, idx) =>
            idx === index ? value : question
          ),
        },
      },
    }))
  }

  const handleDeleteSkillQuestion = (skill, level, index) => {
    setSkillQuestions(prev => {
      const current = prev[skill] || { questions: { beginner: [], intermediate: [], advanced: [] } }
      const updatedLevel = current.questions[level].filter((_, idx) => idx !== index)

      return {
        ...prev,
        [skill]: {
          ...current,
          questions: {
            ...current.questions,
            [level]: updatedLevel.length ? updatedLevel : [""],
          },
        },
      }
    })
  }

  const handleAddSkillQuestion = (skill, level) => {
    setSkillQuestions(prev => {
      const current = prev[skill] || { questions: { beginner: [], intermediate: [], advanced: [] } }
      const nextQuestions = [...(current.questions[level] || []), ""]
      setFocusTarget({ type: "skill", skill, level, index: nextQuestions.length - 1 })
      return {
        ...prev,
        [skill]: {
          ...current,
          questions: {
            ...current.questions,
            [level]: nextQuestions,
          },
        },
      }
    })
  }

  const handleEditSectionQuestion = (sectionName, level, index, value) => {
    setSectionQuestions(prev => ({
      ...prev,
      [sectionName]: {
        ...prev[sectionName],
        questions: {
          ...prev[sectionName].questions,
          [level]: prev[sectionName].questions[level].map((question, idx) =>
            idx === index ? value : question
          ),
        },
      },
    }))
  }

  const handleDeleteSectionQuestion = (sectionName, level, index) => {
    setSectionQuestions(prev => {
      const current = prev[sectionName] || { questions: { beginner: [], intermediate: [], advanced: [] } }
      const updatedLevel = current.questions[level].filter((_, idx) => idx !== index)

      return {
        ...prev,
        [sectionName]: {
          ...current,
          questions: {
            ...current.questions,
            [level]: updatedLevel.length ? updatedLevel : [""],
          },
        },
      }
    })
  }

  const handleAddSectionQuestion = (sectionName, level) => {
    setSectionQuestions(prev => {
      const current = prev[sectionName] || { questions: { beginner: [], intermediate: [], advanced: [] } }
      const nextQuestions = [...(current.questions[level] || []), ""]
      setFocusTarget({ type: "section", sectionName, level, index: nextQuestions.length - 1 })
      return {
        ...prev,
        [sectionName]: {
          ...current,
          questions: {
            ...current.questions,
            [level]: nextQuestions,
          },
        },
      }
    })
  }

  const mainTabButtons = [
    { key: "second", label: "Screening Call Questions" },
    { key: "ai", label: "Talent Scan Questions" },
  ]
  const subTabButtons = [
    { key: "skills", label: "Skill Wise Questions" },
    { key: "sections", label: "Section Wise Questions" },
  ]
  if (isTokenValid === null) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f3f7fb",
        }}
      >
        <div style={{ textAlign: "center", color: "#103d63" }}>
          <Spinner color="primary" />
          <div style={{ marginTop: 12 }}>Validating your access...</div>
        </div>
      </div>
    );
  }

  // 2. Token is invalid
  if (isTokenValid === false) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f3f7fb",
        }}
      >
        <Card
          className="shadow-sm border-0"
          style={{
            width: "100%",
            maxWidth: 520,
            borderRadius: 28,
            background: "#ffffff",
            textAlign: "center",
            padding: "52px 32px",
          }}
        >
          <CardBody className="p-0">
            <img
              src={AtsLogo}
              alt="Bharat ATS"
              style={{ width: 220, maxWidth: "100%", marginBottom: 28 }}
            />
            <h2
              style={{
                marginBottom: 12,
                color: "#103d63",
                fontWeight: 700,
              }}
            >
              Already Reviewed
            </h2>
            <p style={{ color: "#64748b" }}>
              Questions for this job have already been reviewed
            </p>
          </CardBody>
        </Card>
      </div>
    );
  }
  if (isApproved) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background:
            "radial-gradient(circle at top, #eef7ff 0%, #f8fbff 42%, #ffffff 100%)",
        }}
      >
        <Card
          className="shadow-sm border-0"
          style={{
            width: "100%",
            maxWidth: 520,
            borderRadius: 28,
            background: "#ffffff",
            textAlign: "center",
            padding: "52px 32px",
          }}
        >
          <CardBody className="p-0">
            <img
              src={AtsLogo}
              alt="Bharat ATS"
              style={{ width: 220, maxWidth: "100%", marginBottom: 28 }}
            />
            <h2
              style={{
                marginBottom: 12,
                color: "#103d63",
                fontWeight: 700,
              }}
            >
              Approved Successfully
            </h2>
          </CardBody>
        </Card>
      </div>
    )
  }

  if (loadingJob) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "24px",
          background: "#f3f7fb",
        }}
      >
        <div style={{ textAlign: "center", color: "#103d63" }}>
          <Spinner color="primary" />
          <div style={{ marginTop: 12 }}>Loading review questions...</div>
        </div>
      </div>
    )
  }

  return (
    <div
      style={{
        minHeight: "100vh",
        padding: "40px 24px 32px",
        background: "#f3f7fb",
      }}
    >
      <Container fluid style={{ maxWidth: "1240px" }}>
        <Row className="justify-content-center">
          <Col xs={12}>
            <Card
              className="shadow-sm border-0"
              style={{
                borderRadius: 20,
                overflow: "hidden",
                background:
                  "linear-gradient(180deg, #f8fbff 0%, #ffffff 18%, #ffffff 100%)",
              }}
            >
              <div style={{ padding: "24px 32px", background: "#f8fafc" }}>
                <h4 className="mb-1 text-center" style={{ color: "#103d63" }}>
                  Job Owner Screening Questions
                </h4>
                <div className="text-muted text-center">
                  Review the questions shared for this job.
                </div>
              </div>
              <CardBody className="p-1 p-md-3">
                <div
                  className="mb-4"
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #dbe7f3",
                    borderRadius: 18,
                    padding: 24,
                  }}
                >
                  <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                    <div>
                      <h5 className="mb-1" style={{ color: "#103d63" }}>
                        {job?.job_title || "Job Description"}
                      </h5>
                      <div className="text-muted small">
                        Role context for the reviewer before questions are finalized.
                      </div>
                    </div>
                  </div>
                  <div
                    style={{
                      background: "#fff",
                      border: "1px solid #e5edf6",
                      borderRadius: 14,
                      padding: 18,
                      color: "#334155",
                      lineHeight: 1.7,
                      whiteSpace: "pre-wrap",
                      maxHeight: "220px",
                      overflowY: "auto",
                    }}
                  >
                    {jobDescription || "No job description available."}
                  </div>
                </div>

                <div className="mb-4" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                  {mainTabButtons.map(tab => (
                    <Button
                      key={tab.key}
                      color={mainTab === tab.key ? "primary" : "secondary"}
                      outline={mainTab !== tab.key}
                      onClick={() => {
                        setMainTab(tab.key)
                        if (tab.key === "ai") setSubTab("skills")
                      }}
                      style={{
                        minWidth: 220,
                        padding: "10px 18px",
                        borderRadius: 8,
                        fontWeight: 600,
                        fontSize: 14,
                      }}
                    >
                      {tab.label}
                    </Button>
                  ))}
                </div>

                {mainTab === "ai" && (
                  <div className="mb-4" style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    {subTabButtons.map(tab => (
                      <Button
                        key={tab.key}
                        color={subTab === tab.key ? "primary" : "secondary"}
                        outline={subTab !== tab.key}
                        onClick={() => setSubTab(tab.key)}
                        style={{ minWidth: 180, borderRadius: 20, fontWeight: 600 }}
                      >
                        {tab.label}
                      </Button>
                    ))}
                  </div>
                )}

                <div
                  className="p-3 p-md-4"
                  style={{
                    border: "1px solid #dbe7f3",
                    borderRadius: 18,
                    background:
                      "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
                    maxHeight: "58vh",
                    overflowY: "auto",
                    overflowX: "hidden",
                  }}
                >
                  {mainTab === "second" && (
                    <>
                      {questionCategories.map(category => (
                        <div
                          key={category}
                          className="mb-4"
                          style={{
                            background: "#fff",
                            border: "1px solid #e5edf6",
                            borderRadius: 16,
                            padding: 18,
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                            <Label
                              className="fw-bold mb-0"
                              style={{ color: "#103d63" }}
                            >
                              {formatCategoryLabel(category)}
                            </Label>
                            <Button
                              size="sm"
                              className="blueBtn"
                              onClick={() => handleAddQuestion(category)}
                              style={{ minWidth: 138, justifyContent: "center" }}
                            >
                              <FaPlus className="me-1" /> Add Question
                            </Button>
                          </div>

                          {screeningQuestions[category].map((question, index) => (
                            <div
                              key={`${category}-${index}`}
                              className="mb-2"
                              style={{
                                display: "grid",
                                gridTemplateColumns: "minmax(0, 1fr) 48px",
                                gap: 10,
                                alignItems: "center",
                              }}
                            >
                              <Input
                                type="text"
                                value={question}
                                title={question}
                                autoFocus={
                                  focusTarget?.type === "second" &&
                                  focusTarget.category === category &&
                                  focusTarget.index === index
                                }
                                onChange={e =>
                                  handleEditQuestion(
                                    category,
                                    index,
                                    e.target.value
                                  )
                                }
                                style={{
                                  borderRadius: 12,
                                  minHeight: 44,
                                  borderColor: "#d6e2ee",
                                }}
                              />
                              <Button
                                color="danger"
                                title="Delete Question"
                                onClick={() =>
                                  handleDeleteQuestion(category, index)
                                }
                                style={{
                                  width: 48,
                                  height: 44,
                                  padding: 0,
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <FaRegTrashAlt />
                              </Button>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  )}

                  {mainTab === "ai" && subTab === "skills" && (
                    <>
                      {Object.keys(skillQuestions).length === 0 ? (
                        <div className="text-muted">No skill-wise questions available.</div>
                      ) : (
                        Object.entries(skillQuestions).map(([skill, details]) => (
                          <div
                            key={skill}
                            className="mb-4"
                            style={{
                              background: "#fff",
                              border: "1px solid #e5edf6",
                              borderRadius: 16,
                              padding: 18,
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                              <div>
                                <Label className="fw-bold mb-0" style={{ color: "#103d63" }}>
                                  {skill}
                                </Label>
                                {/* <div className="text-muted small">
                                  Skill level: {details.level || "N/A"}
                                </div> */}
                              </div>
                            </div>

                            {Object.entries(details.questions).map(([level, questions]) => (
                              <div key={level} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <Label className="fw-semibold mb-0" style={{ color: "#0f4c81" }}>
                                    {formatCategoryLabel(level)} Questions
                                  </Label>
                                  <Button
                                    size="sm"
                                    className="blueBtn"
                                    onClick={() => handleAddSkillQuestion(skill, level)}
                                  >
                                    <FaPlus className="me-1" /> Add
                                  </Button>
                                </div>

                                {questions.map((question, index) => (
                                  <div
                                    key={`${skill}-${level}-${index}`}
                                    className="mb-2"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) 48px",
                                      gap: 10,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Input
                                      type="text"
                                      value={question}
                                      title={question}
                                      autoFocus={
                                        focusTarget?.type === "skill" &&
                                        focusTarget.skill === skill &&
                                        focusTarget.level === level &&
                                        focusTarget.index === index
                                      }
                                      onChange={e =>
                                        handleEditSkillQuestion(
                                          skill,
                                          level,
                                          index,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        borderRadius: 12,
                                        minHeight: 44,
                                        borderColor: "#d6e2ee",
                                      }}
                                    />
                                    <Button
                                      color="danger"
                                      title="Delete Question"
                                      onClick={() =>
                                        handleDeleteSkillQuestion(
                                          skill,
                                          level,
                                          index
                                        )
                                      }
                                      style={{
                                        width: 48,
                                        height: 44,
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <FaRegTrashAlt />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </>
                  )}

                  {mainTab === "ai" && subTab === "sections" && (
                    <>
                      {Object.keys(sectionQuestions).length === 0 ? (
                        <div className="text-muted">No section-wise questions available.</div>
                      ) : (
                        Object.entries(sectionQuestions).map(([sectionName, section]) => (
                          <div
                            key={sectionName}
                            className="mb-4"
                            style={{
                              background: "#fff",
                              border: "1px solid #e5edf6",
                              borderRadius: 16,
                              padding: 18,
                            }}
                          >
                            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3">
                              <Label className="fw-bold mb-0" style={{ color: "#103d63" }}>
                                {formatCategoryLabel(sectionName)}
                              </Label>
                            </div>

                            {Object.entries(section.questions).map(([level, questions]) => (
                              <div key={level} className="mb-3">
                                <div className="d-flex justify-content-between align-items-center mb-2">
                                  <Label className="fw-semibold mb-0" style={{ color: "#0f4c81" }}>
                                    {formatCategoryLabel(level)} Questions
                                  </Label>
                                  <Button
                                    size="sm"
                                    className="blueBtn"
                                    onClick={() => handleAddSectionQuestion(sectionName, level)}
                                  >
                                    <FaPlus className="me-1" /> Add
                                  </Button>
                                </div>

                                {questions.map((question, index) => (
                                  <div
                                    key={`${sectionName}-${level}-${index}`}
                                    className="mb-2"
                                    style={{
                                      display: "grid",
                                      gridTemplateColumns: "minmax(0, 1fr) 48px",
                                      gap: 10,
                                      alignItems: "center",
                                    }}
                                  >
                                    <Input
                                      type="text"
                                      value={question}
                                      title={question}
                                      autoFocus={
                                        focusTarget?.type === "section" &&
                                        focusTarget.sectionName === sectionName &&
                                        focusTarget.level === level &&
                                        focusTarget.index === index
                                      }
                                      onChange={e =>
                                        handleEditSectionQuestion(
                                          sectionName,
                                          level,
                                          index,
                                          e.target.value
                                        )
                                      }
                                      style={{
                                        borderRadius: 12,
                                        minHeight: 44,
                                        borderColor: "#d6e2ee",
                                      }}
                                    />
                                    <Button
                                      color="danger"
                                      title="Delete Question"
                                      onClick={() =>
                                        handleDeleteSectionQuestion(
                                          sectionName,
                                          level,
                                          index
                                        )
                                      }
                                      style={{
                                        width: 48,
                                        height: 44,
                                        padding: 0,
                                        display: "inline-flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <FaRegTrashAlt />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            ))}
                          </div>
                        ))
                      )}
                    </>
                  )}
                </div>

                <div
                  className="d-flex justify-content-center gap-2 mt-4 pt-3"
                  style={{ borderTop: "1px solid #e8eef5" }}
                >
                  <Button
                      // color="blueBtn"
                    className="blueBtn"
                      // outline
                    onClick={() => setShowPreview(true)}
                  >
                    Preview
                  </Button>
                  <Button
                    className="blueBtn"
                    onClick={handleFinalize}
                    disabled={finalizing}
                  >
                    {finalizing ? "Approving..." : "Approve"}
                  </Button>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
      <Modal isOpen={showPreview} toggle={() => setShowPreview(false)} size="lg" centered>
        <ModalHeader toggle={() => setShowPreview(false)}>
          Questions Preview
        </ModalHeader>

        <ModalBody style={{ maxHeight: "65vh", overflowY: "auto" }}>

          {/* MAIN TABS */}
          <div className="d-flex gap-2 mb-3">
            <Button
              size="sm"
              color={previewTab === "second" ? "primary" : "secondary"}
              onClick={() => setPreviewTab("second")}
            >
              Screening Call Questions
            </Button>

            <Button
              size="sm"
              color={previewTab === "talent" ? "primary" : "secondary"}
              onClick={() => setPreviewTab("talent")}
            >
              Talent Scan Questions
            </Button>
          </div>

          {/* SUB TABS (ONLY FOR TALENT) */}
          {previewTab === "talent" && (
            <div className="d-flex gap-2 mb-3">
              <Button
                size="sm"
                color={talentTab === "skills" ? "primary" : "secondary"}
                onClick={() => setTalentTab("skills")}
              >
                Skill Questions
              </Button>

              <Button
                size="sm"
                color={talentTab === "sections" ? "primary" : "secondary"}
                onClick={() => setTalentTab("sections")}
              >
                Section Questions
              </Button>
            </div>
          )}

          {/* ================= IVR QUESTIONS ================= */}
          {previewTab === "second" &&
            Object.entries(screeningQuestions).map(([cat, qs]) => (
              <div key={cat} className="mb-3">
                <strong>{formatCategoryLabel(cat)}</strong>
                <ol style={{ paddingLeft: "18px" }}>
                  {qs.filter(q => q.trim()).map((q, i) => (
                    <li key={i}>{q}</li>
                  ))}
                </ol>
              </div>
            ))}

          {/* ================= SKILL QUESTIONS ================= */}
          {previewTab === "talent" && talentTab === "skills" &&
            Object.entries(skillQuestions).map(([skill, data]) => (
              <div key={skill} className="mb-3">
                <strong>{skill}</strong>

                {Object.entries(data.questions).map(([level, qs]) => (
                  <div key={level}>
                    <div style={levelBadgeStyle(level)}>
                      {formatCategoryLabel(level)}
                    </div>

                    <ol style={{ paddingLeft: "18px" }}>
                      {qs.filter(q => q.trim()).map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ))}

          {/* ================= SECTION QUESTIONS ================= */}
          {previewTab === "talent" && talentTab === "sections" &&
            Object.entries(sectionQuestions).map(([sec, data]) => (
              <div key={sec} className="mb-3">
                <strong>{sec}</strong>

                {Object.entries(data.questions).map(([level, qs]) => (
                  <div key={level}>
                    <div style={levelBadgeStyle(level)}>
                      {formatCategoryLabel(level)}
                    </div>

                    <ol style={{ paddingLeft: "18px" }}>
                      {qs.filter(q => q.trim()).map((q, i) => (
                        <li key={i}>{q}</li>
                      ))}
                    </ol>
                  </div>
                ))}
              </div>
            ))}

        </ModalBody>
      </Modal>
    </div>
  )
}

export default JobOwnerScreeningQuestions
